import { tracked } from "@glimmer/tracking";
import Component, { setComponentTemplate } from "@ember/component";
import { hash } from "@ember/helper";
import EmberObject, { action, computed } from "@ember/object";
import { getOwner } from "@ember/owner";
import { schedule, throttle } from "@ember/runloop";
import { service } from "@ember/service";
import { classNameBindings } from "@ember-decorators/component";
import { observes, on } from "@ember-decorators/object";
import { BasePlugin } from "@uppy/core";
import $ from "jquery";
import { resolveAllShortUrls } from "pretty-text/upload-short-url";
import { gt } from "truth-helpers";
import DEditor from "discourse/components/d-editor";
import DEditorPreview from "discourse/components/d-editor-preview";
import Wrapper from "discourse/components/form-template-field/wrapper";
import PickFilesButton from "discourse/components/pick-files-button";
import PostTranslationEditor from "discourse/components/post-translation-editor";
import lazyHash from "discourse/helpers/lazy-hash";
import { ajax } from "discourse/lib/ajax";
import { tinyAvatar } from "discourse/lib/avatar-utils";
import { setupComposerPosition } from "discourse/lib/composer/composer-position";
import discourseComputed, { bind, debounce } from "discourse/lib/decorators";
import prepareFormTemplateData from "discourse/lib/form-template-validation";
import { fetchUnseenHashtagsInContext, linkSeenHashtagsInContext } from "discourse/lib/hashtag-decorator";
import { iconHTML } from "discourse/lib/icon-library";
import discourseLater from "discourse/lib/later";
import { fetchUnseenMentions, linkSeenMentions } from "discourse/lib/link-mentions";
import { loadOneboxes } from "discourse/lib/load-oneboxes";
import { generateCookFunction } from "discourse/lib/text";
import { applyValueTransformer } from "discourse/lib/transformer";
import { authorizesOneOrMoreImageExtensions, IMAGE_MARKDOWN_REGEX } from "discourse/lib/uploads";
import UppyComposerUpload from "discourse/lib/uppy/composer-upload";
import { formatUsername } from "discourse/lib/utilities";
import Composer from "discourse/models/composer";
import { i18n } from "discourse-i18n";
import FormTemplateChooser from "select-kit/components/form-template-chooser";
import { createTemplateFactory } from "@ember/template-factory";
let uploadHandlers = [];
export function addComposerUploadHandler(extensions, method) {
  uploadHandlers.push({
    extensions,
    method
  });
}
export function cleanUpComposerUploadHandler() {
  // we cannot set this to uploadHandlers = [] because that messes with
  // the references to the original array that the component has. this only
  // really affects tests, but without doing this you could addComposerUploadHandler
  // in a beforeEach function in a test but then it's not adding to the
  // existing reference that the component has, because an earlier test ran
  // cleanUpComposerUploadHandler and lost it. setting the length to 0 empties
  // the array but keeps the reference
  uploadHandlers.length = 0;
}
let uploadPreProcessors = [];
export function addComposerUploadPreProcessor(pluginClass, optionsResolverFn) {
  if (!(pluginClass.prototype instanceof BasePlugin)) {
    throw new Error("Composer upload preprocessors must inherit from the Uppy BasePlugin class.");
  }
  uploadPreProcessors.push({
    pluginClass,
    optionsResolverFn
  });
}
export function cleanUpComposerUploadPreProcessor() {
  uploadPreProcessors = [];
}
let uploadMarkdownResolvers = [];
export function addComposerUploadMarkdownResolver(resolver) {
  uploadMarkdownResolvers.push(resolver);
}
export function cleanUpComposerUploadMarkdownResolver() {
  uploadMarkdownResolvers = [];
}
let apiImageWrapperBtnEvents = [];
export function addApiImageWrapperButtonClickEvent(fn) {
  apiImageWrapperBtnEvents.push(fn);
}
const DEBOUNCE_FETCH_MS = 450;
const DEBOUNCE_JIT_MS = 2000;
const ComposerEditor = dt7948.c(class ComposerEditor extends Component {
  static #_ = (() => dt7948.g(this.prototype, "composer", [service]))();
  #composer = (() => (dt7948.i(this, "composer"), void 0))();
  static #_2 = (() => dt7948.g(this.prototype, "siteSettings", [service]))();
  #siteSettings = (() => (dt7948.i(this, "siteSettings"), void 0))();
  static #_3 = (() => dt7948.g(this.prototype, "currentUser", [service]))();
  #currentUser = (() => (dt7948.i(this, "currentUser"), void 0))();
  static #_4 = (() => dt7948.g(this.prototype, "preview", [tracked]))();
  #preview = (() => (dt7948.i(this, "preview"), void 0))();
  composerEventPrefix = "composer";
  shouldBuildScrollMap = true;
  scrollMap = null;
  fileUploadElementId = "file-uploader";
  init() {
    super.init(...arguments);
    this.warnedCannotSeeMentions = [];
    this.warnedGroupMentions = [];
    this.uppyComposerUpload = new UppyComposerUpload(getOwner(this), {
      composerEventPrefix: this.composerEventPrefix,
      composerModel: this.composer.model,
      uploadMarkdownResolvers,
      uploadPreProcessors,
      uploadHandlers,
      fileUploadElementId: this.fileUploadElementId
    });
  }
  get topic() {
    return this.composer.get("model.topic");
  }
  replyPlaceholder(requiredCategoryMissing) {
    let placeholder = "composer.reply_placeholder_choose_category";
    if (!requiredCategoryMissing) {
      const key = authorizesOneOrMoreImageExtensions(this.currentUser.staff, this.siteSettings) ? "reply_placeholder" : "reply_placeholder_no_images";
      placeholder = `composer.${key}`;
    }
    return applyValueTransformer("composer-editor-reply-placeholder", placeholder, {
      model: this.composer
    });
  }
  static #_5 = (() => dt7948.n(this.prototype, "replyPlaceholder", [discourseComputed("composer.model.requiredCategoryMissing")]))();
  showLink() {
    return this.currentUser && this.currentUser.link_posting_access !== "none";
  }
  static #_6 = (() => dt7948.n(this.prototype, "showLink", [discourseComputed]))();
  setFocus() {
    if (this.composer.focusTarget === "editor") {
      this.textManipulation.putCursorAtEnd();
    }
  }
  static #_7 = (() => dt7948.n(this.prototype, "setFocus", [observes("composer.focusTarget")]))();
  markdownOptions() {
    return {
      previewing: true,
      formatUsername,
      lookupAvatarByPostNumber: (postNumber, topicId) => {
        const topic = this.topic;
        if (!topic) {
          return;
        }
        const posts = topic.get("postStream.posts");
        if (posts && topicId === topic.get("id")) {
          const quotedPost = posts.findBy("post_number", postNumber);
          if (quotedPost) {
            return tinyAvatar(quotedPost.get("avatar_template"));
          }
        }
      },
      lookupPrimaryUserGroupByPostNumber: (postNumber, topicId) => {
        const topic = this.topic;
        if (!topic) {
          return;
        }
        const posts = topic.get("postStream.posts");
        if (posts && topicId === topic.get("id")) {
          const quotedPost = posts.findBy("post_number", postNumber);
          if (quotedPost) {
            return quotedPost.primary_group_name;
          }
        }
      },
      hashtagTypesInPriorityOrder: this.site.hashtag_configurations["topic-composer"],
      hashtagIcons: this.site.hashtag_icons
    };
  }
  static #_8 = (() => dt7948.n(this.prototype, "markdownOptions", [discourseComputed]))();
  _composerEditorInit() {
    const preview = this.element.querySelector(".d-editor-preview-wrapper");
    this._registerImageAltTextButtonClick(preview);
    if (this.composer.allowUpload) {
      this.uppyComposerUpload.setup(this.element);
    }
    this.appEvents.trigger(`${this.composerEventPrefix}:will-open`);
  }
  /**
  * Sets up the editor with the given text manipulation instance
  *
  * @param {TextManipulation} textManipulation The text manipulation instance
  * @returns {(() => void)} destructor function
  */
  static #_9 = (() => dt7948.n(this.prototype, "_composerEditorInit", [on("didInsertElement")]))();
  setupEditor(textManipulation) {
    this.textManipulation = textManipulation;
    this.uppyComposerUpload.placeholderHandler = textManipulation.placeholder;
    const input = this.element.querySelector(".d-editor-input");
    input.addEventListener("scroll", this._throttledSyncEditorAndPreviewScroll);
    this.composer.set("allowPreview", this.textManipulation.allowPreview);
    if (
    // Focus on the editor unless we have a title
    !this.get("composer.model.canEditTitle") ||
    // Or focus is in the body (e.g. when the editor is destroyed)
    document.activeElement.tagName === "BODY") {
      this.textManipulation.putCursorAtEnd();
    }
    const destroyComposerPosition = setupComposerPosition(input);
    return () => {
      destroyComposerPosition();
      input.removeEventListener("scroll", this._throttledSyncEditorAndPreviewScroll);
    };
  }
  static #_10 = (() => dt7948.n(this.prototype, "setupEditor", [bind]))();
  validation(reply, replyLength, missingReplyCharacters, minimumPostLength, lastValidatedAt) {
    const postType = this.get("composer.post.post_type");
    if (postType === this.site.get("post_types.small_action")) {
      return;
    }
    let reason;
    if (replyLength < 1) {
      reason = i18n("composer.error.post_missing");
    } else if (missingReplyCharacters > 0) {
      reason = i18n("composer.error.post_length", {
        count: minimumPostLength
      });
      const tl = this.get("currentUser.trust_level");
      if ((tl === 0 || tl === 1) && !this._isNewTopic) {
        reason += "<br/>" + i18n("composer.error.try_like", {
          heart: iconHTML("heart", {
            label: i18n("likes_lowercase", {
              count: 1
            })
          })
        });
      }
    }
    if (reason) {
      return EmberObject.create({
        failed: true,
        reason,
        lastShownAt: lastValidatedAt
      });
    }
  }
  static #_11 = (() => dt7948.n(this.prototype, "validation", [discourseComputed("composer.model.reply", "composer.model.replyLength", "composer.model.missingReplyCharacters", "composer.model.minimumPostLength", "composer.lastValidatedAt")]))();
  get _isNewTopic() {
    return this.composer.model.creatingTopic || this.composer.model.editingFirstPost || this.composer.model.creatingSharedDraft;
  }
  static #_12 = (() => dt7948.n(this.prototype, "_isNewTopic", [computed("composer.{creatingTopic,editingFirstPost,creatingSharedDraft}")]))();
  _resetShouldBuildScrollMap() {
    this.set("shouldBuildScrollMap", true);
  }
  _handleInputInteraction(event) {
    const preview = this.element.querySelector(".d-editor-preview-wrapper");
    if (!$(preview).is(":visible")) {
      return;
    }
    preview.removeEventListener("scroll", this._handleInputOrPreviewScroll);
    event.target.addEventListener("scroll", this._handleInputOrPreviewScroll);
  }
  static #_13 = (() => dt7948.n(this.prototype, "_handleInputInteraction", [bind]))();
  _handleInputOrPreviewScroll(event) {
    this._syncScroll(this._syncEditorAndPreviewScroll, $(event.target), $(this.element.querySelector(".d-editor-preview-wrapper")));
  }
  static #_14 = (() => dt7948.n(this.prototype, "_handleInputOrPreviewScroll", [bind]))();
  _handlePreviewInteraction(event) {
    this.element.querySelector(".d-editor-input")?.removeEventListener("scroll", this._handleInputOrPreviewScroll);
    event.target?.addEventListener("scroll", this._handleInputOrPreviewScroll);
  }
  static #_15 = (() => dt7948.n(this.prototype, "_handlePreviewInteraction", [bind]))();
  _syncScroll($callback, $input, $preview) {
    if (!this.scrollMap || this.shouldBuildScrollMap) {
      this.set("scrollMap", this._buildScrollMap($input, $preview));
      this.set("shouldBuildScrollMap", false);
    }
    throttle(this, $callback, $input, $preview, this.scrollMap, 20);
  }
  // Adapted from https://github.com/markdown-it/markdown-it.github.io
  _buildScrollMap($input, $preview) {
    let sourceLikeDiv = $("<div />").css({
      position: "absolute",
      height: "auto",
      visibility: "hidden",
      width: $input[0].clientWidth,
      "font-size": $input.css("font-size"),
      "font-family": $input.css("font-family"),
      "line-height": $input.css("line-height"),
      "white-space": $input.css("white-space")
    }).appendTo("body");
    const linesMap = [];
    let numberOfLines = 0;
    $input.val().split("\n").forEach(text => {
      linesMap.push(numberOfLines);
      if (text.length === 0) {
        numberOfLines++;
      } else {
        sourceLikeDiv.text(text);
        let height;
        let lineHeight;
        height = parseFloat(sourceLikeDiv.css("height"));
        lineHeight = parseFloat(sourceLikeDiv.css("line-height"));
        numberOfLines += Math.round(height / lineHeight);
      }
    });
    linesMap.push(numberOfLines);
    sourceLikeDiv.remove();
    const previewOffsetTop = $preview.offset().top;
    const offset = $preview.scrollTop() - previewOffsetTop - ($input.offset().top - previewOffsetTop);
    const nonEmptyList = [];
    const scrollMap = [];
    for (let i = 0; i < numberOfLines; i++) {
      scrollMap.push(-1);
    }
    nonEmptyList.push(0);
    scrollMap[0] = 0;
    $preview.find(".preview-sync-line").each((_, element) => {
      let $element = $(element);
      let lineNumber = $element.data("line-number");
      let linesToTop = linesMap[lineNumber];
      if (linesToTop !== 0) {
        nonEmptyList.push(linesToTop);
      }
      scrollMap[linesToTop] = Math.round($element.offset().top + offset);
    });
    nonEmptyList.push(numberOfLines);
    scrollMap[numberOfLines] = $preview[0].scrollHeight;
    let position = 0;
    for (let i = 1; i < numberOfLines; i++) {
      if (scrollMap[i] !== -1) {
        position++;
        continue;
      }
      let top = nonEmptyList[position];
      let bottom = nonEmptyList[position + 1];
      scrollMap[i] = ((scrollMap[bottom] * (i - top) + scrollMap[top] * (bottom - i)) / (bottom - top)).toFixed(2);
    }
    return scrollMap;
  }
  _throttledSyncEditorAndPreviewScroll(event) {
    const $preview = $(this.element.querySelector(".d-editor-preview-wrapper"));
    throttle(this, this._syncEditorAndPreviewScroll, $(event.target), $preview, 20);
  }
  static #_16 = (() => dt7948.n(this.prototype, "_throttledSyncEditorAndPreviewScroll", [bind]))();
  _syncEditorAndPreviewScroll($input, $preview) {
    if (!$input) {
      return;
    }
    if ($input.scrollTop() === 0) {
      $preview.scrollTop(0);
      return;
    }
    const inputHeight = $input[0].scrollHeight;
    const previewHeight = $preview[0].scrollHeight;
    if ($input.height() + $input.scrollTop() + 100 > inputHeight) {
      // cheat, special case for bottom
      $preview.scrollTop(previewHeight);
      return;
    }
    const scrollPosition = $input.scrollTop();
    const factor = previewHeight / inputHeight;
    const desired = scrollPosition * factor;
    $preview.scrollTop(desired + 50);
  }
  _renderMentions(preview) {
    const unseen = linkSeenMentions(preview, this.siteSettings);
    if (unseen.length > 0) {
      this._renderUnseenMentions(preview, unseen);
    } else {
      this._warnMentionedGroups(preview);
      this._warnCannotSeeMention(preview);
    }
  }
  _renderUnseenMentions(preview, unseen) {
    fetchUnseenMentions({
      names: unseen,
      topicId: this.get("composer.model.topic.id"),
      allowedNames: this.get("composer.model.targetRecipients")?.split(",")
    }).then(response => {
      linkSeenMentions(preview, this.siteSettings);
      this._warnMentionedGroups(preview);
      this._warnCannotSeeMention(preview);
      this._warnHereMention(response.here_count);
    });
  }
  static #_17 = (() => dt7948.n(this.prototype, "_renderUnseenMentions", [debounce(DEBOUNCE_FETCH_MS)]))();
  _renderHashtags(preview) {
    const context = this.site.hashtag_configurations["topic-composer"];
    const unseen = linkSeenHashtagsInContext(context, preview);
    if (unseen.length > 0) {
      this._renderUnseenHashtags(preview, unseen, context);
    }
  }
  _renderUnseenHashtags(preview, unseen, context) {
    fetchUnseenHashtagsInContext(context, unseen).then(() => linkSeenHashtagsInContext(context, preview));
  }
  static #_18 = (() => dt7948.n(this.prototype, "_renderUnseenHashtags", [debounce(DEBOUNCE_FETCH_MS)]))();
  _refreshOneboxes(preview) {
    const post = this.get("composer.post");
    // If we are editing a post, we'll refresh its contents once.
    const refresh = post && !post.get("refreshedPost");
    const loaded = loadOneboxes(preview, ajax, this.get("composer.topic.id"), this.get("composer.category.id"), this.siteSettings.max_oneboxes_per_post, refresh);
    if (refresh && loaded > 0) {
      post.set("refreshedPost", true);
    }
  }
  static #_19 = (() => dt7948.n(this.prototype, "_refreshOneboxes", [debounce(DEBOUNCE_FETCH_MS)]))();
  _expandShortUrls(preview) {
    resolveAllShortUrls(ajax, this.siteSettings, preview);
  }
  _decorateCookedElement(preview, helper) {
    this.appEvents.trigger("decorate-non-stream-cooked-element", preview, helper);
  }
  _warnMentionedGroups(preview) {
    schedule("afterRender", () => {
      preview.querySelectorAll(".mention-group[data-mentionable-user-count]").forEach(mention => {
        const {
          name
        } = mention.dataset;
        if (this.warnedGroupMentions.includes(name) || this._isInQuote(mention)) {
          return;
        }
        this.warnedGroupMentions.push(name);
        this.composer.groupsMentioned({
          name,
          userCount: mention.dataset.mentionableUserCount,
          maxMentions: mention.dataset.maxMentions
        });
      });
    });
  }
  // add a delay to allow for typing, so you don't open the warning right away
  // previously we would warn after @bob even if you were about to mention @bob2
  static #_20 = (() => dt7948.n(this.prototype, "_warnMentionedGroups", [debounce(DEBOUNCE_JIT_MS)]))();
  _warnCannotSeeMention(preview) {
    if (this.composer.model?.draftKey === Composer.NEW_PRIVATE_MESSAGE_KEY) {
      return;
    }
    preview.querySelectorAll(".mention[data-reason]").forEach(mention => {
      const {
        name
      } = mention.dataset;
      if (this.warnedCannotSeeMentions.includes(name)) {
        return;
      }
      this.warnedCannotSeeMentions.push(name);
      this.composer.cannotSeeMention({
        name,
        reason: mention.dataset.reason
      });
    });
    preview.querySelectorAll(".mention-group[data-reason]").forEach(mention => {
      const {
        name
      } = mention.dataset;
      if (this.warnedCannotSeeMentions.includes(name)) {
        return;
      }
      this.warnedCannotSeeMentions.push(name);
      this.composer.cannotSeeMention({
        name,
        reason: mention.dataset.reason,
        notifiedCount: mention.dataset.notifiedUserCount,
        isGroup: true
      });
    });
  }
  static #_21 = (() => dt7948.n(this.prototype, "_warnCannotSeeMention", [debounce(DEBOUNCE_JIT_MS)]))();
  _warnHereMention(hereCount) {
    if (!hereCount || hereCount === 0) {
      return;
    }
    this.composer.hereMention(hereCount);
  }
  _handleImageScaleButtonClick(event) {
    if (!event.target.classList.contains("scale-btn")) {
      return;
    }
    const index = parseInt(event.target.closest(".button-wrapper").dataset.imageIndex, 10);
    const scale = event.target.dataset.scale;
    const matchingPlaceholder = this.get("composer.model.reply").match(IMAGE_MARKDOWN_REGEX);
    if (matchingPlaceholder) {
      const match = matchingPlaceholder[index];
      if (match) {
        const replacement = match.replace(IMAGE_MARKDOWN_REGEX, `![$1|$2, ${scale}%$4]($5)`);
        this.appEvents.trigger(`${this.composerEventPrefix}:replace-text`, matchingPlaceholder[index], replacement, {
          regex: IMAGE_MARKDOWN_REGEX,
          index
        });
      }
    }
    event.preventDefault();
    return;
  }
  static #_22 = (() => dt7948.n(this.prototype, "_handleImageScaleButtonClick", [bind]))();
  resetImageControls(buttonWrapper) {
    const imageResize = buttonWrapper.querySelector(".scale-btn-container");
    const imageDelete = buttonWrapper.querySelector(".delete-image-button");
    const readonlyContainer = buttonWrapper.querySelector(".alt-text-readonly-container");
    const editContainer = buttonWrapper.querySelector(".alt-text-edit-container");
    imageResize.removeAttribute("hidden");
    imageDelete.removeAttribute("hidden");
    readonlyContainer.removeAttribute("hidden");
    buttonWrapper.removeAttribute("editing");
    editContainer.setAttribute("hidden", "true");
  }
  commitAltText(buttonWrapper) {
    const index = parseInt(buttonWrapper.getAttribute("data-image-index"), 10);
    const matchingPlaceholder = this.get("composer.model.reply").match(IMAGE_MARKDOWN_REGEX);
    const match = matchingPlaceholder[index];
    const input = buttonWrapper.querySelector("input.alt-text-input");
    const replacement = match.replace(IMAGE_MARKDOWN_REGEX, `![${input.value}|$2$3$4]($5)`);
    this.appEvents.trigger(`${this.composerEventPrefix}:replace-text`, match, replacement);
    this.resetImageControls(buttonWrapper);
  }
  _handleAltTextInputKeypress(event) {
    if (!event.target.classList.contains("alt-text-input")) {
      return;
    }
    if (event.key === "[" || event.key === "]") {
      event.preventDefault();
    }
    if (event.key === "Enter") {
      const buttonWrapper = event.target.closest(".button-wrapper");
      this.commitAltText(buttonWrapper);
    }
  }
  static #_23 = (() => dt7948.n(this.prototype, "_handleAltTextInputKeypress", [bind]))();
  _handleAltTextEditButtonClick(event) {
    if (!event.target.classList.contains("alt-text-edit-btn")) {
      return;
    }
    const buttonWrapper = event.target.closest(".button-wrapper");
    const imageResize = buttonWrapper.querySelector(".scale-btn-container");
    const imageDelete = buttonWrapper.querySelector(".delete-image-button");
    const readonlyContainer = buttonWrapper.querySelector(".alt-text-readonly-container");
    const altText = readonlyContainer.querySelector(".alt-text");
    const editContainer = buttonWrapper.querySelector(".alt-text-edit-container");
    const editContainerInput = editContainer.querySelector(".alt-text-input");
    buttonWrapper.setAttribute("editing", "true");
    imageResize.setAttribute("hidden", "true");
    imageDelete.setAttribute("hidden", "true");
    readonlyContainer.setAttribute("hidden", "true");
    editContainerInput.value = altText.textContent;
    editContainer.removeAttribute("hidden");
    editContainerInput.focus();
    event.preventDefault();
  }
  static #_24 = (() => dt7948.n(this.prototype, "_handleAltTextEditButtonClick", [bind]))();
  _handleAltTextOkButtonClick(event) {
    if (!event.target.classList.contains("alt-text-edit-ok")) {
      return;
    }
    const buttonWrapper = event.target.closest(".button-wrapper");
    this.commitAltText(buttonWrapper);
  }
  static #_25 = (() => dt7948.n(this.prototype, "_handleAltTextOkButtonClick", [bind]))();
  _handleAltTextCancelButtonClick(event) {
    if (!event.target.classList.contains("alt-text-edit-cancel")) {
      return;
    }
    const buttonWrapper = event.target.closest(".button-wrapper");
    this.resetImageControls(buttonWrapper);
  }
  static #_26 = (() => dt7948.n(this.prototype, "_handleAltTextCancelButtonClick", [bind]))();
  _handleImageDeleteButtonClick(event) {
    if (!event.target.classList.contains("delete-image-button")) {
      return;
    }
    const index = parseInt(event.target.closest(".button-wrapper").dataset.imageIndex, 10);
    const matchingPlaceholder = this.get("composer.model.reply").match(IMAGE_MARKDOWN_REGEX);
    this.appEvents.trigger(`${this.composerEventPrefix}:replace-text`, matchingPlaceholder[index], "", {
      regex: IMAGE_MARKDOWN_REGEX,
      index
    });
  }
  static #_27 = (() => dt7948.n(this.prototype, "_handleImageDeleteButtonClick", [bind]))();
  _handleImageGridButtonClick(event) {
    if (!event.target.classList.contains("wrap-image-grid-button")) {
      return;
    }
    const index = parseInt(event.target.closest(".button-wrapper").dataset.imageIndex, 10);
    const reply = this.get("composer.model.reply");
    const matches = reply.match(IMAGE_MARKDOWN_REGEX);
    const closingIndex = index + parseInt(event.target.dataset.imageCount, 10) - 1;
    const textArea = this.element.querySelector(".d-editor-input");
    textArea.selectionStart = reply.indexOf(matches[index]);
    textArea.selectionEnd = reply.indexOf(matches[closingIndex]) + matches[closingIndex].length;
    this.appEvents.trigger(`${this.composerEventPrefix}:apply-surround`, "[grid]", "[/grid]", "grid_surround", {
      useBlockMode: true
    });
  }
  static #_28 = (() => dt7948.n(this.prototype, "_handleImageGridButtonClick", [bind]))();
  _registerImageAltTextButtonClick(preview) {
    if (!preview) {
      return;
    }
    preview.addEventListener("click", this._handleAltTextCancelButtonClick);
    preview.addEventListener("click", this._handleAltTextEditButtonClick);
    preview.addEventListener("click", this._handleAltTextOkButtonClick);
    preview.addEventListener("click", this._handleImageDeleteButtonClick);
    preview.addEventListener("click", this._handleImageGridButtonClick);
    preview.addEventListener("click", this._handleImageScaleButtonClick);
    preview.addEventListener("keypress", this._handleAltTextInputKeypress);
    apiImageWrapperBtnEvents.forEach(fn => preview.addEventListener("click", fn));
  }
  _composerClosed() {
    const preview = this.element.querySelector(".d-editor-preview-wrapper");
    if (this.composer.allowUpload) {
      this.uppyComposerUpload.teardown();
    }
    this.appEvents.trigger(`${this.composerEventPrefix}:will-close`);
    // need to wait a bit for the "slide down" transition of the composer
    discourseLater(() => this.appEvents.trigger(`${this.composerEventPrefix}:closed`), 400);
    preview?.removeEventListener("click", this._handleAltTextCancelButtonClick);
    preview?.removeEventListener("click", this._handleAltTextEditButtonClick);
    preview?.removeEventListener("click", this._handleAltTextOkButtonClick);
    preview?.removeEventListener("click", this._handleImageDeleteButtonClick);
    preview?.removeEventListener("click", this._handleImageGridButtonClick);
    preview?.removeEventListener("click", this._handleImageScaleButtonClick);
    preview?.removeEventListener("keypress", this._handleAltTextInputKeypress);
    apiImageWrapperBtnEvents.forEach(fn => preview?.removeEventListener("click", fn));
  }
  static #_29 = (() => dt7948.n(this.prototype, "_composerClosed", [on("willDestroyElement")]))();
  onExpandPopupMenuOptions(toolbarEvent) {
    const selected = toolbarEvent.selected;
    toolbarEvent.selectText(selected.start, selected.end - selected.start);
    this.composer.storeToolbarState(toolbarEvent);
    window.getSelection().removeAllRanges();
  }
  static #_30 = (() => dt7948.n(this.prototype, "onExpandPopupMenuOptions", [action]))();
  showPreview() {
    this.composer.togglePreview();
  }
  _isInQuote(element) {
    let parent = element.parentElement;
    while (parent && !this._isPreviewRoot(parent)) {
      if (this._isQuote(parent)) {
        return true;
      }
      parent = parent.parentElement;
    }
    return false;
  }
  _isPreviewRoot(element) {
    return element.tagName === "DIV" && element.classList.contains("d-editor-preview");
  }
  _isQuote(element) {
    return element.tagName === "ASIDE" && element.classList.contains("quote");
  }
  extraButtons(toolbar) {
    if (this.composer.allowUpload && this.composer.uploadIcon && this.site.desktopView) {
      toolbar.addButton({
        id: "upload",
        group: "insertions",
        icon: this.composer.uploadIcon,
        title: "upload",
        sendAction: this.showUploadModal
      });
    }
    toolbar.addButton({
      id: "options",
      group: "extras",
      icon: "circle-plus",
      title: "composer.options",
      sendAction: this.onExpandPopupMenuOptions.bind(this),
      popupMenu: {
        options: () => this.composer.popupMenuOptions,
        action: this.composer.onPopupMenuAction
      }
    });
  }
  static #_31 = (() => dt7948.n(this.prototype, "extraButtons", [action]))();
  previewUpdated(preview, helper) {
    this._renderMentions(preview);
    this._renderHashtags(preview);
    this._refreshOneboxes(preview);
    this._expandShortUrls(preview);
    this._decorateCookedElement(preview, helper);
    this.composer.afterRefresh(preview);
  }
  static #_32 = (() => dt7948.n(this.prototype, "previewUpdated", [action]))();
  get selectedFormTemplateId() {
    if (this._selectedFormTemplateId) {
      return this._selectedFormTemplateId;
    }
    return this.composer.model.formTemplateId || this.composer.formTemplateIds?.[0];
  }
  static #_33 = (() => dt7948.n(this.prototype, "selectedFormTemplateId", [computed("composer.formTemplateIds")]))();
  set selectedFormTemplateId(value) {
    this._selectedFormTemplateId = value;
  }
  get showTranslationEditor() {
    if (!this.siteSettings.content_localization_enabled || !this.currentUser.can_localize_content) {
      return false;
    }
    if (this.composer.model?.action === Composer.ADD_TRANSLATION) {
      return true;
    }
    return false;
  }
  async updateFormPreview() {
    const formTemplateData = prepareFormTemplateData(document.querySelector("#form-template-form"), this.composer.selectedFormTemplate, false);
    this.preview = await this.cachedCookAsync(formTemplateData, this.markdownOptions);
  }
  static #_34 = (() => dt7948.n(this.prototype, "updateFormPreview", [action]))();
  async cachedCookAsync(text, options) {
    this._cachedCookFunction ||= await generateCookFunction(options || {});
    return await this._cachedCookFunction(text);
  }
  updateSelectedFormTemplateId(formTemplateId) {
    this.selectedFormTemplateId = formTemplateId;
  }
  static #_35 = (() => dt7948.n(this.prototype, "updateSelectedFormTemplateId", [action]))();
  showFormTemplateForm(formTemplateIds, replyingToTopic, editingPost) {
    return formTemplateIds?.length > 0 && !replyingToTopic && !editingPost;
  }
  static #_36 = (() => dt7948.n(this.prototype, "showFormTemplateForm", [discourseComputed("composer.formTemplateIds", "composer.model.replyingToTopic", "composer.model.editingPost")]))();
  showUploadModal() {
    document.getElementById(this.fileUploadElementId).click();
  }
  static #_37 = (() => dt7948.n(this.prototype, "showUploadModal", [action]))();
  static #_38 = (() => setComponentTemplate(createTemplateFactory(
  /*
    
      {{#if this.showFormTemplateForm}}
        <div class="d-editor">
          <div class="d-editor-textarea-column">
            {{yield}}
  
            {{#if (gt this.composer.formTemplateIds.length 1)}}
              <FormTemplateChooser
                @filteredIds={{this.composer.formTemplateIds}}
                @value={{this.selectedFormTemplateId}}
                @onChange={{this.updateSelectedFormTemplateId}}
                @options={{hash maximum=1}}
                class="composer-select-form-template"
              />
            {{/if}}
            <form id="form-template-form">
              <Wrapper
                @id={{this.selectedFormTemplateId}}
                @initialValues={{this.composer.formTemplateInitialValues}}
                @onSelectFormTemplate={{this.composer.onSelectFormTemplate}}
                @onChange={{this.updateFormPreview}}
              />
            </form>
          </div>
          {{#if this.siteSettings.show_preview_for_form_templates}}
            <DEditorPreview
              @preview={{this.preview}}
              @forcePreview={{this.forcePreview}}
              @onPreviewUpdated={{this.previewUpdated}}
              @outletArgs={{this.outletArgs}}
            />
          {{/if}}
        </div>
      {{else if this.showTranslationEditor}}
        <PostTranslationEditor @setupEditor={{this.setupEditor}} />
      {{else}}
        <DEditor
          @value={{this.composer.model.reply}}
          @placeholder={{this.replyPlaceholder}}
          @previewUpdated={{this.previewUpdated}}
          @markdownOptions={{this.markdownOptions}}
          @extraButtons={{this.extraButtons}}
          @processPreview={{this.composer.isPreviewVisible}}
          @validation={{this.validation}}
          @loading={{this.composer.loading}}
          @forcePreview={{this.forcePreview}}
          @showLink={{this.showLink}}
          @composerEvents={{true}}
          @onPopupMenuAction={{this.composer.onPopupMenuAction}}
          @popupMenuOptions={{this.composer.popupMenuOptions}}
          @disabled={{this.composer.disableTextarea}}
          @outletArgs={{lazyHash
            composer=this.composer.model
            editorType="composer"
          }}
          @topicId={{this.composer.model.topic.id}}
          @categoryId={{this.composer.model.category.id}}
          @replyingToUserId={{this.composer.replyingToUserId}}
          @onSetup={{this.setupEditor}}
          @disableSubmit={{this.composer.disableSubmit}}
        >
          {{yield}}
        </DEditor>
      {{/if}}
  
      {{#if this.composer.allowUpload}}
        <PickFilesButton
          @fileInputId={{this.fileUploadElementId}}
          @allowMultiple={{true}}
          name="file-uploader"
        />
      {{/if}}
    
  */
  {
    "id": "prgAttXE",
    "block": "[[[1,\"\\n\"],[41,[30,0,[\"showFormTemplateForm\"]],[[[1,\"      \"],[10,0],[14,0,\"d-editor\"],[12],[1,\"\\n        \"],[10,0],[14,0,\"d-editor-textarea-column\"],[12],[1,\"\\n          \"],[18,1,null],[1,\"\\n\\n\"],[41,[28,[32,0],[[30,0,[\"composer\",\"formTemplateIds\",\"length\"]],1],null],[[[1,\"            \"],[8,[32,1],[[24,0,\"composer-select-form-template\"]],[[\"@filteredIds\",\"@value\",\"@onChange\",\"@options\"],[[30,0,[\"composer\",\"formTemplateIds\"]],[30,0,[\"selectedFormTemplateId\"]],[30,0,[\"updateSelectedFormTemplateId\"]],[28,[32,2],null,[[\"maximum\"],[1]]]]],null],[1,\"\\n\"]],[]],null],[1,\"          \"],[10,\"form\"],[14,1,\"form-template-form\"],[12],[1,\"\\n            \"],[8,[32,3],null,[[\"@id\",\"@initialValues\",\"@onSelectFormTemplate\",\"@onChange\"],[[30,0,[\"selectedFormTemplateId\"]],[30,0,[\"composer\",\"formTemplateInitialValues\"]],[30,0,[\"composer\",\"onSelectFormTemplate\"]],[30,0,[\"updateFormPreview\"]]]],null],[1,\"\\n          \"],[13],[1,\"\\n        \"],[13],[1,\"\\n\"],[41,[30,0,[\"siteSettings\",\"show_preview_for_form_templates\"]],[[[1,\"          \"],[8,[32,4],null,[[\"@preview\",\"@forcePreview\",\"@onPreviewUpdated\",\"@outletArgs\"],[[30,0,[\"preview\"]],[30,0,[\"forcePreview\"]],[30,0,[\"previewUpdated\"]],[30,0,[\"outletArgs\"]]]],null],[1,\"\\n\"]],[]],null],[1,\"      \"],[13],[1,\"\\n\"]],[]],[[[41,[30,0,[\"showTranslationEditor\"]],[[[1,\"      \"],[8,[32,5],null,[[\"@setupEditor\"],[[30,0,[\"setupEditor\"]]]],null],[1,\"\\n\"]],[]],[[[1,\"      \"],[8,[32,6],null,[[\"@value\",\"@placeholder\",\"@previewUpdated\",\"@markdownOptions\",\"@extraButtons\",\"@processPreview\",\"@validation\",\"@loading\",\"@forcePreview\",\"@showLink\",\"@composerEvents\",\"@onPopupMenuAction\",\"@popupMenuOptions\",\"@disabled\",\"@outletArgs\",\"@topicId\",\"@categoryId\",\"@replyingToUserId\",\"@onSetup\",\"@disableSubmit\"],[[30,0,[\"composer\",\"model\",\"reply\"]],[30,0,[\"replyPlaceholder\"]],[30,0,[\"previewUpdated\"]],[30,0,[\"markdownOptions\"]],[30,0,[\"extraButtons\"]],[30,0,[\"composer\",\"isPreviewVisible\"]],[30,0,[\"validation\"]],[30,0,[\"composer\",\"loading\"]],[30,0,[\"forcePreview\"]],[30,0,[\"showLink\"]],true,[30,0,[\"composer\",\"onPopupMenuAction\"]],[30,0,[\"composer\",\"popupMenuOptions\"]],[30,0,[\"composer\",\"disableTextarea\"]],[28,[32,7],null,[[\"composer\",\"editorType\"],[[30,0,[\"composer\",\"model\"]],\"composer\"]]],[30,0,[\"composer\",\"model\",\"topic\",\"id\"]],[30,0,[\"composer\",\"model\",\"category\",\"id\"]],[30,0,[\"composer\",\"replyingToUserId\"]],[30,0,[\"setupEditor\"]],[30,0,[\"composer\",\"disableSubmit\"]]]],[[\"default\"],[[[[1,\"\\n        \"],[18,1,null],[1,\"\\n      \"]],[]]]]],[1,\"\\n    \"]],[]]]],[]]],[1,\"\\n\"],[41,[30,0,[\"composer\",\"allowUpload\"]],[[[1,\"      \"],[8,[32,8],[[24,3,\"file-uploader\"]],[[\"@fileInputId\",\"@allowMultiple\"],[[30,0,[\"fileUploadElementId\"]],true]],null],[1,\"\\n\"]],[]],null],[1,\"  \"]],[\"&default\"],false,[\"if\",\"yield\"]]",
    "moduleName": "/var/www/discourse/app/assets/javascripts/discourse/node_modules/.embroider/rewritten-app/components/composer-editor.js",
    "scope": () => [gt, FormTemplateChooser, hash, Wrapper, DEditorPreview, PostTranslationEditor, DEditor, lazyHash, PickFilesButton],
    "isStrictMode": true
  }), this))();
}, [classNameBindings("composer.showToolbar:toolbar-visible", ":wmd-controls")]);
export default ComposerEditor;